import { useEffect, useMemo, useRef } from "react";
import { getAccessToken } from "../../../../../api/authService";
import { useUserContext } from "../../../../../contexts/UserContext";
import { getCompanyIdByStoreId } from "../../../helpers/storeHelpers";
import { getENV } from "../../../../../utils";
import { ReactEnvironments } from "../../../../common/constants";

// Only DEV is verified to work. Other environments are untested.
const TSO = {
  LOCAL_URL: "http://localhost:3001",
  DEV_URL: "https://app-fixedops-parts-tso-ui.app-dev.connectcdk.com",
  DIT_URL: "https://app-fixedops-parts-tso-ui.app-dit.connectcdk.com",
  STAGE_URL: "https://app-fixedops-parts-tso-ui.app-stage.connectcdk.com",
  PERF_URL: "https://app-fixedops-parts-tso-ui.app-perf.connectcdk.com",
  PROD_URL: "https://app-fixedops-parts-tso-ui.app.connectcdk.com",
};

const getTSOOrigin = (env: string = getENV()): string => {
  switch (env) {
    case ReactEnvironments.Production:
      return TSO.PROD_URL;
    case ReactEnvironments.Staging:
      return TSO.STAGE_URL;
    case ReactEnvironments.Perf:
      return TSO.PERF_URL;
    case ReactEnvironments.DIT:
      return TSO.DIT_URL;
    case ReactEnvironments.Develop:
      return TSO.DEV_URL;
    case ReactEnvironments.Local:
      return TSO.LOCAL_URL;
    default:
      console.warn("[CDCM] TSO origin not found. Defaulting to DEV.", env);
      return TSO.DEV_URL;
  }
};

// TODO: Except Custom Pricing need to remove other setups
const CUSTOM_PRICING_PATH: Record<string, string> = {
  CustomPricing: "custom-pricing",
};

const IFRAME_STYLES = {
  width: "100%",
  height: "100%",
  border: "none",
  minHeight: "80vh",
} as const;

enum ParentMessageType {
  INIT = "CDCM_INIT",
}

enum ChildMessageType {
  READY = "TSO_READY",
}

type InitializationPayload = {
  accessToken: string;
  storeId: string | null;
};

type ChildMessage = {
  type: ChildMessageType.READY;
};

const sendInitializationToIframe = (
  iframe: HTMLIFrameElement | null,
  payload: InitializationPayload,
  targetOrigin: string,
): void => {

  iframe?.contentWindow?.postMessage(
    {
      type: ParentMessageType.INIT,
      payload,
    },
    targetOrigin,
  );
};

export default function CustomPricingEmbed(): JSX.Element {
  const { selected, iamUserState, defaultStore } = useUserContext();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const companyId = getCompanyIdByStoreId(
    selected.store.value,
    iamUserState.extendedAccountInfo,
  );

  const targetOrigin = useMemo(() => getTSOOrigin(), []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<ChildMessage>) => {
      if (event.origin !== targetOrigin) {
        return;
      }

      if (event.data?.type !== ChildMessageType.READY) {
        return;
      }

      sendInitializationToIframe(
        iframeRef.current,
        {
          accessToken: getAccessToken(),
          storeId: defaultStore.value ?? null,
        },
        targetOrigin,
      );
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [targetOrigin, defaultStore.value]);

  if (!companyId) {
    return <div>Unable to load pricing setup.</div>;
  }

  const tsoUrl = `${targetOrigin}/fixedops-parts-tso/${companyId}/${CUSTOM_PRICING_PATH[selected.setup.value]}`;

  return (
    <iframe
      ref={iframeRef}
      src={tsoUrl}
      title={selected.setup.label}
      style={IFRAME_STYLES}
      data-testid="heavy-truck-pricing-embed"
    />
  );
}
